import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "estimate-calculator" }
const _hoisted_2 = { class: "description" }
const _hoisted_3 = {
  key: 0,
  class: "estimated-disbursements"
}
const _hoisted_4 = { class: "disbursements" }
const _hoisted_5 = { class: "amount" }
const _hoisted_6 = { class: "term" }
const _hoisted_7 = { class: "info-tile" }

import { ref, computed, onMounted } from "vue";
import useFormatter from "@/use/formatter";
import Icon from "@/components/shared/Icon.vue";
import Step from "@/components/shared/Step.vue";
import EstimateCalculator from "./EstimateCalculator.vue";
import Tile from "@/components/shared/Tile.vue";
import NextButton from "@/components/shared/NextButton.vue";
import Modal from "@/components/shared/Modal.vue";
import CreditAvailabilityNotice from "@/components/shared/CreditAvailabilityNotice.vue";
import { useStore } from "vuex";
import BorrowAmountLOC from "./BorrowAmountLOC.vue";
import useApplicationData from "@/composables/useApplicationData";

interface Props {
  application: any;
  term?: string;
  initialDrawRequest?: any;
  loading?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BorrowAmount',
  props: {
    application: {},
    term: { default: undefined },
    initialDrawRequest: { default: undefined },
    loading: { type: Boolean, default: false }
  },
  emits: ["previous", "close", "complete"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const store = useStore();

const { studentType } = useApplicationData();

// Template Refs
const borrowAmountRef = ref<InstanceType<typeof BorrowAmountLOC>>();

// Props
const props = __props;

// Emit
const emit = __emit;

const { formatNumber } = useFormatter();

// Refs
const displayEstimateCalculator = ref<boolean>(false);

// Computed
const amount = computed<number>(() => {
  return borrowAmountRef.value?.values?.amount;
});

const saveChangesDisabled = computed(() => {
  return borrowAmountRef.value?.hasErrors || props.loading;
});

const appData = computed(() => store.state.appData);

const selectedTerm = computed<string>(() => {
  return store?.state?.appData?.application?.schoolConfiguration?.academicTerm
    ? store?.state?.appData?.application?.schoolConfiguration?.academicTerm
    : "Not Yet Selected";
});

const combinedTerms = computed<boolean>(() => {
  return (store?.state?.appData?.application?.schoolConfiguration?.academicTerm || "").includes(
    "-",
  );
});

const disbursements = computed(() => {
  const terms = props.term?.split("-") || [];
  return terms.map((term: string) => {
    return {
      term: term.trim(),
      amount: (amount.value || 0) / (terms || []).length,
    };
  });
});

const terms = computed<string>(() => {
  const terms = props.term?.split("-") || [];
  let termString = "";
  for (let i = 0; i < terms.length; i++) {
    if (i === 0) {
      termString += terms[i].trim();
    } else if (i > 0 && i < terms.length - 1) {
      termString += `, ${terms[i].trim()}`;
    } else if (i === terms.length - 1) {
      termString += ` and ${terms[i].trim()}`;
    }
  }
  return termString;
});

const requestedAmount = computed<number | null>(() => {
  if (amount.value === undefined || amount.value === null || isNaN(Number(amount.value))) {
    return null;
  }
  return Number(amount.value);
});

const requestedAmountChanged = computed<boolean>(() => {
  const original = store.state.loanConfigurationModule?.applicationInfo?.loanAmount;

  if (original === requestedAmount.value) {
    return false;
  }

  return true;
});

// Methods
function useThisAmount(event: string | number | undefined) {
  borrowAmountRef.value?.updateAmount(event);
}

function setReqAmount() {
  borrowAmountRef.value?.updateAmount(props.initialDrawRequest?.amount || undefined, false);
}

function closeModal() {
  emit("close", true);
}

function toggleCalculator() {
  displayEstimateCalculator.value = !displayEstimateCalculator.value;
}

function goBackToPortal() {
  emit("complete", { amount: amount.value, requestedAmountChanged: requestedAmountChanged.value });
}

onMounted(() => {
  setReqAmount();
});

__expose({
  amount,
  terms,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    allowCloseBtn: true,
    onClose: closeModal
  }, {
    default: _withCtx(() => [
      _createVNode(Tile, null, {
        default: _withCtx(() => [
          _createVNode(Step, null, {
            header: _withCtx(() => [...(_cache[1] || (_cache[1] = [
              _createTextVNode(" How Much Do You Need for Your Selected Term? ", -1)
            ]))]),
            actions: _withCtx(() => [
              _createVNode(NextButton, {
                loading: __props.loading,
                disabled: saveChangesDisabled.value,
                onClick: goBackToPortal,
                text: "Save Changes"
              }, null, 8, ["loading", "disabled"])
            ]),
            default: _withCtx(() => [
              _cache[5] || (_cache[5] = _createElementVNode("p", null, " These funds can be used for your school's total cost of attendance which typically includes tuition, fees, books, room & board, food & transportation. ", -1)),
              _createVNode(CreditAvailabilityNotice),
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("p", null, [
                    _cache[2] || (_cache[2] = _createTextVNode(" Amount for ", -1)),
                    _createElementVNode("b", null, _toDisplayString(selectedTerm.value), 1),
                    _createTextVNode(" " + _toDisplayString(combinedTerms.value ? "combined" : ""), 1)
                  ]),
                  _createElementVNode("a", { onClick: toggleCalculator }, "How much do I need?")
                ]),
                _createElementVNode("div", {
                  class: _normalizeClass(["calculator-tooltip", { open: displayEstimateCalculator.value }])
                }, [
                  _createVNode(EstimateCalculator, {
                    term: __props.term || '',
                    application: __props.application,
                    onUseThisAmount: _cache[0] || (_cache[0] = ($event: any) => (useThisAmount($event))),
                    onClose: toggleCalculator
                  }, null, 8, ["term", "application"])
                ], 2)
              ]),
              _createVNode(BorrowAmountLOC, {
                ref_key: "borrowAmountRef",
                ref: borrowAmountRef,
                disabled: __props.loading
              }, null, 8, ["disabled"]),
              _cache[6] || (_cache[6] = _createElementVNode("div", { class: "hint" }, "(If you are unsure, enter 0.)", -1)),
              (disbursements.value.length > 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    _cache[3] || (_cache[3] = _createElementVNode("p", null, "Estimated disbursements to be sent to your school:", -1)),
                    _createElementVNode("div", _hoisted_4, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(disbursements.value, (disbursement, index) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: index,
                          class: "disbursement"
                        }, [
                          _createElementVNode("p", _hoisted_5, "$" + _toDisplayString(_unref(formatNumber)(disbursement.amount)), 1),
                          _createElementVNode("p", _hoisted_6, _toDisplayString(disbursement.term), 1)
                        ]))
                      }), 128))
                    ])
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_7, [
                _createVNode(Icon, { icon: "info" }),
                _cache[4] || (_cache[4] = _createElementVNode("p", null, [
                  _createTextVNode(" You can use this line of credit over multiple years "),
                  _createElementVNode("b", null, "without"),
                  _createTextVNode(" reapplying each year ")
                ], -1)),
                _createElementVNode("p", null, [
                  _createTextVNode(" Your maximum line of credit for your " + _toDisplayString(_unref(studentType)) + " degree is ", 1),
                  _createElementVNode("b", null, "$" + _toDisplayString(_unref(formatNumber)(appData.value?.application?.loanAmount)), 1)
                ])
              ])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})